// =============================================================================
// io/parsers/platforms.ts — TopstepX, Tradesea, MotiveWave.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3):
// `parseTopstepX`, `adaptTradeseaFillRow`, `parseTradesea`, `parseMotiveWave`.
//
// The last three formats the old client reads and this one did not. Until now a
// file from any of them was reported as unrecognised, by name, in the import
// dialog — the honest failure, but a failure.
//
// ── THE THREE ARE NOT THE SAME KIND OF WORK ──
//
//   TopstepX   a PAIRED export — one row per finished round trip. Its own
//              parser, like Tradovate's Performance Report.
//   Tradesea   per-fill, in TWO different shapes. An ADAPTER per row into the
//              shape the position-episode walker already reads.
//   MotiveWave per-fill, and already column-compatible with that walker. One
//              fix-up, then straight through.
//
// ⚠ THAT IS WHY TWO OF THEM COST ALMOST NOTHING. The walkers are the expensive
// part and both already exist; a new per-fill source is a column mapping. The
// old client's own note says as much, and it is the reason these were grouped as
// one task rather than three.
//
// ── WHAT TRADESEA AND MOTIVEWAVE GAIN BY GOING THROUGH THE EPISODE WALKER ──
//
// VWAP entry/exit across scale-ins, and an `executions[]` timeline — so Peak
// Capture and exact give-back work on their trades (D83). Routing them through
// the per-fill FIFO walker instead would have been simpler to write and would
// have produced trades that can never be peak-reconstructed.
//
// ⚠ NEITHER carries an account name; the user assigns one at the account-mapping
// step. Neither carries a contract MONTH either, so `contractFull` is the root —
// the same "continuous contract" approximation every root-only source gets.
// =============================================================================

import { getCol, numCol, numColSigned, type Row } from './columns';
import { parseAtasFillsFromRows, type AtasFillsResult } from './atas';
import { buildImportedTrade, fullContractSym, type ImportedTrade } from './sierra';
import { findContract, type ContractSpec } from '../../domain/trade';
import { parseDateOnly, parseDelimited, parseTimeOnly } from './delimited';

// ---------------------------------------------------------------------------
// TopstepX — a paired round-trip journal export
// ---------------------------------------------------------------------------

/**
 * Whether a header set is TopstepX's journal export.
 *
 * `EnteredAt` + `ExitedAt` + `ContractName` — all three, because the first two
 * alone are generic enough to appear on a hand-rolled spreadsheet.
 */
export function looksLikeTopstepX(headers: readonly string[]): boolean {
  const h = new Set(headers.map((x) => x.trim().toLowerCase().replace(/[\s_\-/#]/g, '')));
  return h.has('enteredat') && h.has('exitedat') && h.has('contractname');
}

/**
 * TopstepX — one row per complete round trip.
 *
 * Columns: `Id`, `ContractName`, `EnteredAt`, `ExitedAt`, `EntryPrice`,
 * `ExitPrice`, `Fees`, `Commissions`, `PnL`, `Size`, `Type`, `TradeDay`,
 * `TradeDuration`.
 *
 * ⚠ `PnL` IS GROSS, BEFORE COST — unlike most paired exports, where the P&L
 * column is already net. TopstepX then splits the cost in two: `Fees` (exchange,
 * clearing, NFA) and `Commissions` (Topstep's own per-side rate × qty). Net is
 * `PnL − (Fees + Commissions)`. Reading `PnL` as net would overstate every
 * winner and understate every loser by the round-turn cost — on a 1-lot MES
 * scalp that is most of the trade.
 *
 * ⚠ `ContractName` is a CME code with the month and year on it (`ESM6` = ES,
 * June 2026). It is NOT stripped here: `findContract` prefix-matches to the
 * root, and `contractFull` wants the dated symbol. Stripping would throw away
 * the one source of a real contract month.
 *
 * ⚠ THE TIMESTAMP'S OFFSET IS IGNORED, as in the old client. `EnteredAt` reads
 * `MM/DD/YYYY HH:MM:SS ±HH:MM`, and the time stored is the WALL CLOCK as
 * written, not converted. Converting here would put this client's trade times an
 * hour or more off the same trades imported into beta.
 *
 * ⚠ `netAuthoritative` is deliberately NOT set, matching the old client, so
 * `buildImportedTrade`'s sign check still runs. It is a no-op when the broker's
 * sign is right and a repair when it is not.
 */
export function parseTopstepX(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): ImportedTrade[] {
  const trades: ImportedTrade[] = [];

  rows.forEach((r, idx) => {
    let sym = String(getCol(r, 'ContractName', 'Contract', 'Symbol') || '').trim();
    if (!sym) return;
    sym = sym.replace(/\s+/g, '');

    // ⚠ An unreadable side is a DROPPED ROW, not a defaulted long. Guessing here
    // inverts the points and the P&L of every short in the file.
    const typeStr = String(getCol(r, 'Type', 'Side', 'Direction') || '').toLowerCase().trim();
    const side: 'long' | 'short' | null = typeStr.startsWith('long') ? 'long'
      : typeStr.startsWith('short') ? 'short' : null;
    if (!side) return;

    const enteredAt = getCol(r, 'EnteredAt', 'Entered At', 'Entry Time');
    const exitedAt = getCol(r, 'ExitedAt', 'Exited At', 'Exit Time');
    if (!enteredAt || !exitedAt) return;

    const entry = numCol(r, 'EntryPrice', 'Entry Price');
    const exit = numCol(r, 'ExitPrice', 'Exit Price');
    if (!entry || !exit) return;

    const qty = parseInt(
      String(getCol(r, 'Size', 'Qty', 'Quantity') || '1').replace(/,/g, ''), 10,
    ) || 1;

    // ⚠ `numColSigned`, not `numCol`. TopstepX writes bare negatives, but the
    // accounting `(123.45)` form costs nothing to handle and reading one with
    // `numCol` records a loss as a win with no error anywhere.
    const grossPnl = numColSigned(r, 'PnL', 'P/L', 'Profit');
    const totalFees = numCol(r, 'Fees') + numCol(r, 'Commissions', 'Commission');
    const netPnl = grossPnl - totalFees;

    const c = findContract(sym, catalogue);
    const realId = getCol(r, 'Id', 'TradeId', 'Trade ID', 'Trade Id');
    // ⚠ The synthesised id carries the ROW INDEX. Two identical scalps a second
    // apart are two trades, and a content-only fingerprint collapses them into
    // one on re-import. Same reasoning as the Tradovate paired path.
    const closeFillId = realId
      ? `topstepx:${realId}`
      : `topstepx-rt:${idx}:${exitedAt}:${exit}:${qty}`;

    trades.push(buildImportedTrade({
      date: parseDateOnly(enteredAt, today),
      timeIn: parseTimeOnly(enteredAt),
      timeOut: parseTimeOnly(exitedAt),
      contract: c?.symbol || sym.toUpperCase(),
      contractFull: fullContractSym(sym),
      side, qty, entry, exit,
      fees: totalFees,
      // ⚠ 0 becomes null so a genuinely scratched trade is rebuilt from
      // entry/exit rather than stored as a hard zero. The old client's rule.
      netPnl: netPnl !== 0 ? netPnl : null,
      dpp: c?.dollarsPerPoint ?? 0,
      sourceAccount: getCol(r, 'Account', 'AccountId', 'Account ID') || null,
      openFillId: null, closeFillId,
      maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
    }));
  });

  return trades.filter((t) => t.contract);
}

// ---------------------------------------------------------------------------
// Tradesea — two export shapes, one adapter
// ---------------------------------------------------------------------------

/** Whether a header set is either Tradesea shape. */
export function looksLikeTradesea(headers: readonly string[]): boolean {
  const h = new Set(headers.map((x) => x.trim().toLowerCase()));
  const notifications = h.has('eventtype') && h.has('updatetype') && h.has('instrument');
  const orders = h.has('symbol') && h.has('side') && h.has('order type')
    && h.has('avg price') && h.has('status');
  return notifications || orders;
}

/**
 * One Tradesea row → the shape the position-episode walker reads, or `null` to
 * drop it.
 *
 * ── TWO SHAPES, AND THE ROW ITSELF SAYS WHICH ──
 *
 *   notifications event-log  every order and position lifecycle event is a row:
 *                            `place_order`, `order_working`, `stop_loss_updated`,
 *                            `position_opened`… ⚠ ONLY `orderUpdate` /
 *                            `order_filled` rows are executions. Everything else
 *                            is intent, not a fill — importing them would invent
 *                            trades from orders that never filled. No commission
 *                            column, so fees rebuild as 0.
 *   orders/fills export      one row per fill, with a real `Commission`. Keep
 *                            `Status=Filled`; Working and Canceled are dropped
 *                            for the same reason.
 *
 * ⚠ THE BRANCH IS ON WHETHER THE EVENT COLUMNS EXIST, not on a file-level guess.
 * A row with `eventType`/`updateType` is judged as an event; a row without them
 * falls to the status test. That is what lets one adapter serve both.
 *
 * ⚠ `CME:MES` → `MES`. The exchange prefix has to go or `findContract` resolves
 * nothing, and the trade lands with no $/point and therefore no P&L.
 */
export function adaptTradeseaFillRow(r: Row, today: string): Row | null {
  const evt = String(getCol(r, 'eventType') || '').toLowerCase();
  const upd = String(getCol(r, 'updateType') || '').toLowerCase();
  if (evt || upd) {
    if (evt !== 'orderupdate' || upd !== 'order_filled') return null;
  } else {
    const status = String(getCol(r, 'Status') || '').toLowerCase();
    if (status && status !== 'filled') return null;
  }

  const inst = String(getCol(r, 'instrument', 'Instrument', 'symbol', 'Symbol') || '')
    .replace(/^[A-Za-z0-9]+:/, '').trim();
  const side = String(getCol(r, 'side', 'Side', 'Direction') || '').trim();
  const px = numCol(r, 'avgPrice', 'Avg Price', 'AvgPrice', 'Price', 'FillPrice');
  const qty = getCol(r, 'filledQty', 'FilledQty', 'qty', 'Qty', 'Volume');
  const ts = String(getCol(r, 'timestamp', 'Time', 'DateTime') || '').trim();
  const fee = getCol(r, 'commission', 'Commission', 'Fees');
  if (!inst || !side || !px || !qty) return null;

  const date = parseDateOnly(ts, today);
  const time = parseTimeOnly(ts);

  return {
    // ⚠ No account. Both shapes are account-less; the user maps one afterwards.
    Account: '',
    Instrument: inst,
    // Buy / Sell → the walker's `/^b/` test.
    Direction: side,
    Volume: qty,
    Price: String(px),
    Commission: fee || '',
    // ⚠ Rewritten to `YYYY-MM-DD HH:MM:SS` rather than passed through: the
    // walker SORTS on `new Date(Time)`, and the notifications log is
    // newest-first. A format the sort cannot read leaves it in file order, and a
    // FIFO walk over reversed fills pairs every exit against the wrong entry.
    Time: (date && time) ? `${date} ${time}` : (date || ts),
    Id: getCol(r, 'orderId', 'OrderId', 'Id') || '',
  };
}

export function parseTradesea(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): AtasFillsResult {
  const fills = rows
    .map((r) => adaptTradeseaFillRow(r, today))
    .filter((x): x is Row => x !== null);
  return parseAtasFillsFromRows(fills, catalogue, today);
}

// ---------------------------------------------------------------------------
// MotiveWave — per-fill Executions export
// ---------------------------------------------------------------------------

/** Whether a header set is MotiveWave's Executions export. */
export function looksLikeMotiveWave(headers: readonly string[]): boolean {
  const h = new Set(headers.map((x) => x.trim().toLowerCase()));
  return h.has('underlying') && h.has('symbol') && h.has('action') && h.has('ticket');
}

/**
 * MotiveWave Executions — already column-compatible with the episode walker.
 *
 * Header: `Account, Ticket, Order ID, Underlying, Symbol, Exchange, Time,
 * Action, Quantity, Price, Commission, Fill Id`.
 *
 * The walker's alias lists already cover it: `Symbol` → Instrument (`ESU6`
 * resolves to ES by prefix), `Action` → Direction (`BOT` matches `/^b/`, `SLD`
 * does not), `Quantity` → Volume, `Commission` → Fees.
 *
 * ⚠ THE ONE FIX-UP: `Fill Id` IS BLANK ON SIM AND PAPER EXPORTS. `Ticket` is
 * unique per fill, so it is stamped into `Fill Id` when that is empty. Without
 * it every sim fill has an empty id, which breaks re-import de-duplication and
 * leaves the `executions[]` timeline unattributable.
 *
 * ⚠ `Order ID` is NOT a substitute and is not read as one. One order can produce
 * several fills; using it as the fill id would make partial fills look like
 * duplicates of each other.
 */
export function stampMotiveWaveFillIds(rows: readonly Row[]): Row[] {
  return rows.map((r) => {
    if (getCol(r, 'Id', 'Fill Id', 'FillId')) return r;
    const ticket = getCol(r, 'Ticket');
    return ticket ? { ...r, 'Fill Id': ticket } : r;
  });
}

export function parseMotiveWave(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): AtasFillsResult {
  return parseAtasFillsFromRows(stampMotiveWaveFillIds(rows), catalogue, today);
}

/** Convenience for the whole-text path, matching the other parsers' shape. */
export function parseMotiveWaveText(
  text: string,
  catalogue: readonly ContractSpec[],
  today: string,
  delim = ',',
): AtasFillsResult {
  return parseMotiveWave(parseDelimited(text, delim), catalogue, today);
}
